import { logError } from "services/logging";
import { AiExerciseSequence } from "./exercises";

export const names = {
  createExercises: "create_exercises",
  topicSummary: "topic_summary",
  endConversation: "end_conversation",
  updatePreferences: "update_conversation_preferences",
} as const;

export type UpdatePreferencesArgs = {
  speaking_pace?: "slower" | "faster";
  turn_patience?: "more" | "less";
  reset?: boolean;
};

type Eagerness = "low" | "medium" | "high" | "auto";

export type ResolvedPreferences = {
  turn_detection: { type: "semantic_vad"; eagerness: Eagerness };
  output_speed: number;
};

export type CreateExercisesArgs = {
  context: string;
};

export type CreateExercisesOutput =
  | {
      type: typeof names.createExercises;
      exercise_sequence: AiExerciseSequence;
    }
  | { error: string };

export type TopicSummaryArguments = {
  topicTitle: string;
  keyPoints: string;
  examples?: string;
  tip: string;
};

// The skills report Tally produces at the end of a long-enough session.
export type SkillAssessmentReport = {
  motivational_statement: string;
  category_scores: SkillAssessmentCategoryScore[];
  skills_feedback: {
    strengths: StrengthFeedback[];
    improvement: ImprovementFeedback[];
  };
};

// Arguments of the end_conversation tool: the report is optional — a session
// too short to assess ends the call with no arguments.
export type EndConversationArguments = Partial<SkillAssessmentReport>;

export type SkillAssessmentCategoryScore = {
  name:
    | "Foundations"
    | "Share Your Ideas"
    | "Understand Others"
    | "Conversation Skills"
    | "Culture and Context";
  score: number;
  assessed: boolean;
};

type StrengthFeedback = {
  skill_name: string;
  description: string;
  example?: string;
};

type ImprovementFeedback = {
  skill_name: string;
  description: string;
  suggestion: string;
};

export type SkillAssessmentFeedback = StrengthFeedback | ImprovementFeedback;

// `T` appears only in the return, which makes it the caller's assertion about a
// payload nothing here validates. Honest would be `unknown` — but that moves one
// cast to each of the callers rather than removing it, so it stays concentrated.
export const parseFunctionArguments = <T>(args: string): T | null => {
  if (!args) return null;
  try {
    return JSON.parse(args) as T;
  } catch (error) {
    logError("Error parsing function arguments:", { error, args });
    return null;
  }
};

// The end_conversation report, if the session was long enough to produce one.
// A too-short session ends with empty arguments — return null so callers can
// treat it as a plain session-end (no card, no assessment chip).
export const getSkillReport = (args: string): SkillAssessmentReport | null => {
  const data = parseFunctionArguments<EndConversationArguments>(args);
  if (data && Array.isArray(data.category_scores) && data.category_scores.length > 0) {
    return data as SkillAssessmentReport;
  }
  return null;
};

export type ToolCallOutput = CreateExercisesOutput | TopicSummaryArguments;
